import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/shared/providers/AuthProvider";
import { apiRoutes } from "@/shared/api/routes";
import type {
  UserPreferences,
  UserPreferencesApiResponse,
  UserPreferencesState,
  UserPreferenceItem,
  ProfilePreferenceValue,
  ModelPreference,
} from "@/shared/types/preferences.types";
import type { ViewSettings } from "@/shared/components/viewSettings/types";

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

interface UserPreferencesContextType extends UserPreferencesState {
  /** Re-fetch preferences from the server */
  refetchPreferences: () => Promise<void>;
}

const UserPreferencesContext = createContext<
  UserPreferencesContextType | undefined
>(undefined);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Safely parse a preference value.
 * The API returns `value` as a JSON-encoded string, so we JSON.parse it.
 * If it is already an object (or not parseable) we return it as-is / null.
 */
const parsePreferenceValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

const normalizePreferences = (items: UserPreferenceItem[]): UserPreferences => {
  const profileItem = items.find((item) => item.key === "profile");

  let profileValue: ProfilePreferenceValue | null = null;
  if (profileItem) {
    const parsed = parsePreferenceValue(profileItem.value);
    if (parsed !== null && typeof parsed === "object") {
      profileValue = parsed as ProfilePreferenceValue;
    }
  }

  return {
    raw: items,
    profile: profileValue,
    getByKey: (key: string) => items.find((item) => item.key === key),
    getParsedValueByKey: (key: string) => {
      const item = items.find((i) => i.key === key);
      return item !== undefined ? parsePreferenceValue(item.value) : null;
    },
  };
};

/**
 * Build a UserPreferences object from ViewSettings already in the React Query
 * cache. Used for instant post-save sync — no network call needed.
 *
 * The returned object is semantically complete: `raw` contains a synthetic
 * "profile" UserPreferenceItem, and `getByKey` / `getParsedValueByKey` search
 * that item — so any future caller of `preferences.getByKey('profile')` or
 * `preferences.getParsedValueByKey('profile')` gets the correct value instead
 * of undefined / null.
 */
const buildUserPreferencesFromViewSettings = (
  viewSettings: ViewSettings
): UserPreferences => {
  const model_preferences: ModelPreference[] = viewSettings.pageSettings.map((ps) => {
    const mp: ModelPreference = { id: ps.id };
    if (ps.pageSize !== undefined) mp.page_size = ps.pageSize;
    if (ps.search !== undefined) mp.auto_search = ps.search;
    if (ps.columns !== undefined) mp.column_set = ps.columns;
    if (ps.stopFilter !== undefined) mp.stop_filter = ps.stopFilter;
    if (ps.dragAndDrop !== undefined && ps.dragAndDrop !== '') {
      mp.drag_n_drop = ps.dragAndDrop === 'true';
    }
    if (ps.refreshPeriod !== undefined && ps.refreshPeriod !== '') {
      const secondsMap: Record<number, number> = { 1: 60, 2: 120, 5: 300, 10: 600 };
      const secs = secondsMap[ps.refreshPeriod as number];
      if (secs !== undefined) mp.refresh_period = secs;
    }
    return mp;
  });

  const profileValue: ProfilePreferenceValue = {
    view: {
      start_dow: viewSettings.startingDayOfWeek?.toLowerCase(),
      float_header: viewSettings.floatTableHeaders,
      model_preferences,
    },
  };

  // Construct a synthetic UserPreferenceItem for the "profile" key so that
  // getByKey / getParsedValueByKey return correct results for any caller that
  // reads from the context after a Settings save (rather than after the full
  // network fetch via normalizePreferences).
  const profileItem: UserPreferenceItem = {
    id: 0,
    key: "profile",
    value: profileValue,
  };
  const items: UserPreferenceItem[] = [profileItem];

  return {
    raw: items,
    profile: profileValue,
    getByKey: (key: string) => items.find((item) => item.key === key),
    getParsedValueByKey: (key: string) => {
      const item = items.find((i) => i.key === key);
      // profileValue is already a parsed object — return as-is.
      return item !== undefined ? parsePreferenceValue(item.value) : null;
    },
  };
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider: React.FC<
  UserPreferencesProviderProps
> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [state, setState] = useState<UserPreferencesState>({
    preferences: null,
    loading: false,
    error: null,
  });

  // Original network fetch — used for initial load on mount.
  const fetchPreferences = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch(apiRoutes.proxy("session/me/preferences"), {
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Accept-Language": "en-US",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch preferences: ${response.status} ${response.statusText}`
        );
      }

      const json: UserPreferencesApiResponse = await response.json();
      const items: UserPreferenceItem[] = Array.isArray(json?.data)
        ? json.data
        : [];

      setState({
        preferences: normalizePreferences(items),
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({
        preferences: null,
        loading: false,
        error:
          err instanceof Error ? err.message : "Failed to load preferences",
      });
    }
  }, []);

  // Fetch once when the user becomes authenticated (initial load).
  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferences();
    } else {
      setState({ preferences: null, loading: false, error: null });
    }
  }, [isAuthenticated, fetchPreferences]);

  // Post-save fast sync: when the React Query ['preferences'] cache updates
  // (after ViewSettingsModal saves and invalidateQueries fires), immediately
  // derive UserPreferences from the fresh ViewSettings without a second
  // network call. This makes Group B pages (Schedules, Trips, Subscriptions,
  // Clients) update as fast as Group A pages (RunStatus, ArchiveTrips, etc.).
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        event.action?.type === "success" &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === "preferences"
      ) {
        // In TanStack Query v5 the reducer commits new data to query.state.data
        // synchronously before dispatching the cache notification, so
        // query.state.data is the stable public API to read here — no `as any`
        // cast required.
        const fresh = event.query.state.data as ViewSettings | undefined;
        if (fresh && event.query.state.status === "success") {
          setState({
            preferences: buildUserPreferencesFromViewSettings(fresh),
            loading: false,
            error: null,
          });
        }
      }
    });

    return unsubscribe;
  }, [isAuthenticated, queryClient]);

  const contextValue = useMemo<UserPreferencesContextType>(
    () => ({
      ...state,
      refetchPreferences: fetchPreferences,
    }),
    [state, fetchPreferences]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useUserPreferences must be used within a UserPreferencesProvider"
    );
  }
  return context;
};

/**
 * Safe variant of `useUserPreferences` for low-level UI components that may be
 * rendered outside a `UserPreferencesProvider` (e.g. in unit tests or Storybook).
 * Returns `null` instead of throwing when the context is not available.
 */
export const useUserPreferencesSafe = (): UserPreferencesContextType | null => {
  return useContext(UserPreferencesContext) ?? null;
};
